import {MarkdownRenderer} from '@github-ui/copilot-markdown'
import {NewToolHeader, type ToolHeaderPropsType} from './NewToolHeader'
import toolCodeBlockExtension, {type ToolCodeBlockExtensionArgs} from './code-block/ToolCodeBlockExtension'
import {getToolIcon} from './ToolIcon'
import {TOOL_NAMES} from '../../../constants/tool-names'
import {CodeInsightsIndicator} from './code-insights/CodeInsights'
import type {
  AsyncBashToolArgs,
  BashToolArgs,
  Delta,
  GlobToolArgs,
  GrepToolArgs,
  ReadAsyncBashToolArgs,
  ReadBashToolArgs,
  ReplyToCommentToolArgs,
  ReportProgressToolArgs,
  RunCustomSetupStepToolArgs,
  StopAsyncBashToolArgs,
  StopBashToolArgs,
  StrReplaceEditorToolArgs,
  ThinkToolArgs,
  ToolArgs,
  ToolName,
  RunSetupArgs,
  WriteBashToolArgs,
  GitHubMCPServerWebSearchToolArgs,
  GitHubMCPServerPullRequestReadToolArgs,
  WebFetchToolArgs,
  CommitChangesToolArgs,
  ProposeWorkToolArgs,
  SkillToolArgs,
  PowerShellToolArgs,
  ReadPowerShellToolArgs,
  WritePowerShellToolArgs,
  StopPowerShellToolArgs,
  CreatePullRequestToolArgs,
  SearchCodeSubagentToolArgs,
  TaskCompleteToolArgs,
  ReadAgentToolArgs,
  WriteAgentToolArgs,
} from '../../../types/session'
import {extractContentFromDiff} from '../../../utils/extract-content-from-diff'
import {Stack, Text} from '@primer/react'
import type {ReactNode} from 'react'
import {Children, useEffect, useRef, useState} from 'react'
import {SkeletonText} from '@primer/react/experimental'
import {trimFileName} from '../../../utils/trim-file-name'
import styles from './Tool.module.css'
import {clsx} from 'clsx'
import {CollapsibleRegion} from '../CollapsibleRegion'
import {COLLAPSE_ANIMATION_FLAG} from '../../../utils/constants'
import {useSessionLogsContext} from '../Context'
import {parseUnifiedDiff} from '../../../utils/parse-unified-diff'
// eslint-disable-next-line @github-ui/github-monorepo/restrict-relay-imports
import {StaticUnifiedDiffPreview} from '@github-ui/diff-lines/static-unified-diff-preview'
import {isFeatureEnabled} from '@github-ui/feature-flags'
import {ToolProposeWork} from './propose-work/ToolProposeWork'
import {DiffPreview} from './DiffPreview'
import {formatPullRequestReadTitle, resolvePullRequestReadOutputLanguage} from './pull-request-read-utils'
import {buildReadAgentTitle, buildWriteAgentTitle, parseAgentStatusLog} from './agent-tool-utils'

// Maximum character length for markdown content to prevent parser performance issues
const MAX_MARKDOWN_LENGTH = 100_000
// Height threshold for tool content preview - content shorter than this will be shown fully expanded
// Set as a CSS custom property via inline style so both JS and CSS reference the same value
const TOOL_PREVIEW_HEIGHT_THRESHOLD = 475

const wrapInCodeBlock = ({language, markdown}: {language: string; markdown: string}) => {
  if (!markdown) {
    return `~~~\nNothing to display\n~~~`
  }

  // Use ~~~ for the delimiter to avoid conflicts with backticks. This is acceptable by remark-gfm, the underlying rendering library.
  return `~~~${language}\n${markdown}\n~~~`
}

/**
 * Truncates markdown content to prevent the markdown parser from performance issues.
 * Returns the truncated content and a flag indicating if truncation occurred.
 * If truncation fails, returns the original markdown.
 */
function truncateMarkdownContent(
  markdown: string | undefined,
  maxLength: number = MAX_MARKDOWN_LENGTH,
): {markdown: string | undefined} {
  if (!markdown || markdown.length <= maxLength) {
    return {markdown}
  }

  const truncated = markdown.slice(0, maxLength)
  const truncationMessage = `\n\n⚠️ **Output truncated**`

  return {
    markdown: truncated + truncationMessage,
  }
}

const CODE_REVIEW_TITLE = 'Review changes with Copilot code review'
const CODEQL_CHECKER_TITLE = 'Run CodeQL security scan on changed files'
const PARALLEL_VALIDATION_TITLE = 'Check changes with Copilot code review and CodeQL'

/* CCA is in the process of backfilling Snippy data after
 * releasing logs for some time with high false positive rates.
 * To avoid showing inaccurate code insights, we are gating the feature behind both a feature flag and a session creation date check.
 * To be removed with CCA backfill.
 */
const SNIPPY_CUTOFF_DATE = new Date('2026-02-11T22:00:00Z')

// Configures custom titles for tool calls using the default tool call rendering implementation
const TOOL_CALL_TITLES: {[key: string]: string} = {
  // Playwright
  // Covers all of the "Core automation" tools included in the Playwright MCP server
  ['playwright-browser_navigate']: 'Navigate Playwright web browser to a URL',
  ['playwright-browser_navigate_back']: 'Navigate back in Playwright web browser',
  ['playwright-browser_navigate_forward']: 'Navigate forward in Playwright web browser',
  ['playwright-browser_click']: 'Click element in Playwright web browser',
  ['playwright-browser_take_screenshot']: 'Take screenshot of Playwright web browser',
  ['playwright-browser_type']: 'Type in Playwright web browser',
  ['playwright-browser_wait_for']: 'Wait for text to appear/disappear in Playwright web browser',
  ['playwright-browser_evaluate']: 'Run JavaScript in Playwright web browser',
  ['playwright-browser_snapshot']: 'Take snapshot of page in Playwright web browser',
  ['playwright-browser_resize']: 'Resize Playwright web browser window',
  ['playwright-browser_close']: 'Close Playwright web browser',
  ['playwright-browser_press_key']: 'Press key in Playwright web browser',
  ['playwright-browser_select_option']: 'Select option in Playwright web browser',
  ['playwright-browser_handle_dialog']: 'Interact with dialog in Playwright web browser',
  ['playwright-browser_console_messages']: 'Get console messages from Playwright web browser',
  ['playwright-browser_drag']: 'Drag mouse between elements in Playwright web browser',
  ['playwright-browser_file_upload']: 'Upload file in Playwright web browser',
  ['playwright-browser_hover']: 'Hover mouse over element in Playwright web browser',
  ['playwright-browser_network_requests']: 'Get network requests from Playwright web browser',
  // GitHub
  // Covers commonly-used tools in the GitHub MCP Server
  ['github-mcp-server-get_file_contents']: 'Get file contents from GitHub',
  ['github-mcp-server-get_pull_request']: 'Get pull request from GitHub',
  ['github-mcp-server-get_issue']: 'Get issue from GitHub',
  ['github-mcp-server-get_pull_request_files']: 'Get pull request changed files from GitHub',
  ['github-mcp-server-list_pull_requests']: 'List pull requests on GitHub',
  ['github-mcp-server-list_branches']: 'List branches on GitHub',
  ['github-mcp-server-get_pull_request_diff']: 'Get pull request diff from GitHub',
  ['github-mcp-server-get_pull_request_comments']: 'Get pull request comments from GitHub',
  ['github-mcp-server-get_commit']: 'Get commit from GitHub',
  ['github-mcp-server-search_repositories']: 'Search repositories on GitHub',
  ['github-mcp-server-search_code']: 'Search code on GitHub',
  ['github-mcp-server-get_issue_comments']: 'Get issue comments from GitHub',
  ['github-mcp-server-list_issues']: 'List issues on GitHub',
  ['github-mcp-server-search_pull_requests']: 'Search pull requests on GitHub',
  ['github-mcp-server-list_commits']: 'List commits on GitHub',
  ['github-mcp-server-get_pull_request_status']: 'Get pull request status from GitHub',
  ['github-mcp-server-search_issues']: 'Search issues on GitHub',
  ['github-mcp-server-get_pull_request_reviews']: 'Get pull request reviews from GitHub',
  ['github-mcp-server-download_workflow_run_artifact']: 'Download GitHub Actions workflow run artifact',
  ['github-mcp-server-get_job_logs']: 'Get GitHub Actions job logs',
  ['github-mcp-server-get_workflow_run']: 'Get GitHub Actions workflow run',
  ['github-mcp-server-get_workflow_run_logs']: 'Get GitHub Actions workflow run logs',
  ['github-mcp-server-get_workflow_run_usage']: 'Get GitHub Actions workflow usage',
  ['github-mcp-server-list_workflow_jobs']: 'List GitHub Actions workflow jobs',
  ['github-mcp-server-list_workflow_run_artifacts']: 'List GitHub Actions workflow run artifacts',
  ['github-mcp-server-list_workflow_runs']: 'List GitHub Actions workflow runs',
  ['github-mcp-server-list_workflows']: 'List GitHub Actions workflows',
  // Other first party tools
  ['gh-advisory-database']: 'Check new dependencies against the GitHub Advisory Database',
  ['codeql_checker']: CODEQL_CHECKER_TITLE,
  ['code_review']: CODE_REVIEW_TITLE,
  ['store_memory']: 'Store memory',

  // Autofind tools
  ['read_code']: 'Read code',
  ['store_comment']: 'Store comment',
  ['remove_comment']: 'Remove comment',
  ['store_fix']: 'Store fix',
  ['store_skip']: 'Skip fix',
  ['semantic_issue_search']: 'Semantic issue search',
  ['plan']: 'Plan',
  ['search_dir']: 'Search directory',
  ['list_dir']: 'List directory',
  ['search_file']: 'Search file',
}

/**
 * Creates tool header props with optional trailing visual for code insights indicator.
 * Code insights indicator is gated behind copilot_agent_snippy flag.
 */
function createToolHeaderProps(
  baseProps: ToolHeaderPropsType,
  metadata: import('../../../types/tool-log-metadata').ToolLogMetadataBag | undefined,
  shouldShowCodeInsights: boolean,
): ToolHeaderPropsType {
  // Code insights indicator is gated behind ff
  if (shouldShowCodeInsights) {
    const codeInsights = metadata?.['code-insights']
    if (codeInsights && codeInsights.publicCodeReferences.length > 0) {
      return {
        ...baseProps,
        trailingVisual: <CodeInsightsIndicator insights={codeInsights} />,
      }
    }
  }

  return baseProps
}

export function ToolRenderer({
  name,
  delta,
  args,
  mcpToolNameMappings,
  isInGroup,
  isEditGroup,
  metadata,
}: {
  name: ToolName
  delta: Delta
  args: ToolArgs
  mcpToolNameMappings?: Record<string, string>
  isInGroup?: boolean
  isEditGroup?: boolean
  metadata?: import('../../../types/tool-log-metadata').ToolLogMetadataBag
}) {
  const isSnippyEnabled = isFeatureEnabled('copilot_agent_snippy')
  const isSnippyTimeFilteringEnabled = isFeatureEnabled('copilot_agent_snippy_time_filter')

  const {session} = useSessionLogsContext()

  let shouldShowCodeInsights = false
  if (isSnippyTimeFilteringEnabled) {
    shouldShowCodeInsights =
      isSnippyEnabled && session?.created_at !== undefined && new Date(session.created_at) >= SNIPPY_CUTOFF_DATE
  } else {
    shouldShowCodeInsights = isSnippyEnabled
  }

  // Hide icon when tool is in a group (group header shows the icon instead)
  const toolIcon = isInGroup ? undefined : getToolIcon(name, mcpToolNameMappings)
  switch (name) {
    case TOOL_NAMES.BASH: {
      const bashArgs = args as BashToolArgs
      const input = `$ ${bashArgs.command}`
      // When in a group, don't show redundant 'Bash:' prefix
      const title = isInGroup
        ? bashArgs.description || bashArgs.command
        : bashArgs.description
          ? `${bashArgs.description}`
          : `Run Bash command`
      const {isError, content: output} = parseContent(delta.content)
      const isPending = output === undefined

      return (
        <Tool
          title={title}
          isError={isError}
          isPending={isPending}
          hasPreview={false}
          isInGroup={false}
          toolHeaderProps={createToolHeaderProps({leadingVisual: toolIcon}, metadata, shouldShowCodeInsights)}
        >
          <ToolPrimaryContent
            language={bashArgs.command?.startsWith('git diff') ? 'diff' : 'bash'}
            markdown={`${input}${output ? `\n${output}` : ''}`}
            shellLoading={isPending}
          />
        </Tool>
      )
    }

    case TOOL_NAMES.WEB_SEARCH:
    case TOOL_NAMES.GITHUB_MCP_SERVER_WEB_SEARCH:
    case TOOL_NAMES.GITHUB_MCP_SERVER_BING_SEARCH: {
      const webSearchArgs = args as GitHubMCPServerWebSearchToolArgs
      const title = `Web search: ${webSearchArgs.query}`
      const {isError, content: output} = parseContent(delta.content)

      return (
        <Tool
          title={title}
          isError={isError}
          isInGroup={isInGroup}
          toolHeaderProps={createToolHeaderProps({leadingVisual: toolIcon}, metadata, shouldShowCodeInsights)}
        >
          <ToolPrimaryContent language="json" markdown={output} />
        </Tool>
      )
    }

    case TOOL_NAMES.GITHUB_MCP_SERVER_PULL_REQUEST_READ: {
      const prArgs = args as GitHubMCPServerPullRequestReadToolArgs
      const title = formatPullRequestReadTitle(prArgs)
      const {isError, content} = parseContent(delta.content)
      const {language: outputLanguage, output} = resolvePullRequestReadOutputLanguage(
        prArgs?.method,
        content,
        parseGenericToolOutput,
      )
      const isPending = output === undefined
      const input = Object.keys(prArgs ?? {}).length === 0 ? null : JSON.stringify(prArgs, null, 2)

      return (
        <Tool
          title={title}
          isError={isError}
          isPending={isPending}
          isInGroup={isInGroup}
          toolHeaderProps={createToolHeaderProps({leadingVisual: toolIcon}, metadata, shouldShowCodeInsights)}
        >
          {input && <ToolSecondaryContent type="input" language="json" markdown={input} />}
          <ToolPrimaryContent language={outputLanguage} markdown={output} />
        </Tool>
      )
    }

    case TOOL_NAMES.WEB_FETCH: {
      const webFetchArgs = args as WebFetchToolArgs
      const title = `Web fetch: ${webFetchArgs.url}`
      const {isError, content: output} = parseContent(delta.content)

      return (
        <Tool
          title={title}
          isError={isError}
          isInGroup={isInGroup}
          toolHeaderProps={createToolHeaderProps({leadingVisual: toolIcon}, metadata, shouldShowCodeInsights)}
        >
          <ToolPrimaryContent language="json" markdown={output} />
        </Tool>
      )
    }

    case TOOL_NAMES.WRITE_BASH: {
      const writeBashArgs = args as WriteBashToolArgs
      const input = `$ ${writeBashArgs.input}`
      const {isError, content: output} = parseContent(delta.content)
      const isPending = output === undefined

      return (
        <Tool
          title={`Send input to Bash session ${writeBashArgs.shellId || writeBashArgs.sessionId}`}
          isError={isError}
          isPending={isPending}
          isInGroup={isInGroup}
          toolHeaderProps={createToolHeaderProps({leadingVisual: toolIcon}, metadata, shouldShowCodeInsights)}
        >
          <ToolPrimaryContent
            language="bash"
            markdown={`${input}${output ? `\n${output}` : ''}`}
            shellLoading={isPending}
          />
        </Tool>
      )
    }

    case TOOL_NAMES.READ_BASH: {
      const readBashArgs = args as ReadBashToolArgs
      const {isError, content: output} = parseContent(delta.content)
      const isPending = output === undefined

      return (
        <Tool
          title={`Read logs from Bash session ${readBashArgs.shellId || readBashArgs.sessionId}`}
          isError={isError}
          isPending={isPending}
          isInGroup={isInGroup}
          toolHeaderProps={createToolHeaderProps({leadingVisual: toolIcon}, metadata, shouldShowCodeInsights)}
        >
          <ToolPrimaryContent language="bash" markdown={output ? `\n${output}` : undefined} shellLoading={isPending} />
        </Tool>
      )
    }

    case TOOL_NAMES.STOP_BASH: {
      const stopBashArgs = args as StopBashToolArgs
      const {isError, content: output} = parseContent(delta.content)
      const isPending = output === undefined

      return (
        <Tool
          title={`Stop Bash session ${stopBashArgs.shellId || stopBashArgs.sessionId}`}
          isError={isError}
          isPending={isPending}
          isInGroup={isInGroup}
          toolHeaderProps={createToolHeaderProps({leadingVisual: toolIcon}, metadata, shouldShowCodeInsights)}
        >
          <ToolPrimaryContent language="bash" markdown={output} shellLoading={isPending} />
        </Tool>
      )
    }

    case TOOL_NAMES.POWERSHELL: {
      const powershellArgs = args as PowerShellToolArgs
      const input = `PS> ${powershellArgs.command}`
      const title = isInGroup
        ? powershellArgs.description || powershellArgs.command
        : powershellArgs.description
          ? `${powershellArgs.description}`
          : `Run PowerShell command`
      const {isError, content: output} = parseContent(delta.content)
      const isPending = output === undefined

      return (
        <Tool
          title={title}
          isError={isError}
          isPending={isPending}
          hasPreview={false}
          isInGroup={false}
          toolHeaderProps={{leadingVisual: toolIcon}}
        >
          <ToolPrimaryContent
            language={powershellArgs.command?.startsWith('git diff') ? 'diff' : 'powershell'}
            markdown={`${input}${output ? `\n${output}` : ''}`}
            shellLoading={isPending}
          />
        </Tool>
      )
    }

    case TOOL_NAMES.WRITE_POWERSHELL: {
      const writePowershellArgs = args as WritePowerShellToolArgs
      const input = `PS> ${writePowershellArgs.input}`
      const {isError, content: output} = parseContent(delta.content)
      const isPending = output === undefined

      return (
        <Tool
          title={`Send input to PowerShell session ${writePowershellArgs.shellId || writePowershellArgs.sessionId}`}
          isError={isError}
          isPending={isPending}
          isInGroup={isInGroup}
          toolHeaderProps={{leadingVisual: toolIcon}}
        >
          <ToolPrimaryContent
            language="powershell"
            markdown={`${input}${output ? `\n${output}` : ''}`}
            shellLoading={isPending}
          />
        </Tool>
      )
    }

    case TOOL_NAMES.READ_POWERSHELL: {
      const readPowershellArgs = args as ReadPowerShellToolArgs
      const {isError, content: output} = parseContent(delta.content)
      const isPending = output === undefined

      return (
        <Tool
          title={`Read logs from PowerShell session ${readPowershellArgs.shellId || readPowershellArgs.sessionId}`}
          isError={isError}
          isPending={isPending}
          isInGroup={isInGroup}
          toolHeaderProps={{leadingVisual: toolIcon}}
        >
          <ToolPrimaryContent
            language="powershell"
            markdown={output ? `\n${output}` : undefined}
            shellLoading={isPending}
          />
        </Tool>
      )
    }

    case TOOL_NAMES.STOP_POWERSHELL: {
      const stopPowershellArgs = args as StopPowerShellToolArgs
      const {isError, content: output} = parseContent(delta.content)
      const isPending = output === undefined

      return (
        <Tool
          title={`Stop PowerShell session ${stopPowershellArgs.shellId || stopPowershellArgs.sessionId}`}
          isError={isError}
          isPending={isPending}
          isInGroup={isInGroup}
          toolHeaderProps={{leadingVisual: toolIcon}}
        >
          <ToolPrimaryContent language="powershell" markdown={output ? output : ''} shellLoading={isPending} />
        </Tool>
      )
    }

    case TOOL_NAMES.LIST_POWERSHELL: {
      const {isError, content: output} = parseContent(delta.content)
      const isPending = output === undefined

      return (
        <Tool
          title={`List PowerShell sessions`}
          isError={isError}
          isPending={isPending}
          isInGroup={isInGroup}
          toolHeaderProps={{leadingVisual: toolIcon}}
        >
          <ToolPrimaryContent language="powershell" markdown={output ? output : ''} shellLoading={isPending} />
        </Tool>
      )
    }

    case TOOL_NAMES.THINK: {
      const thinkArgs = args as ThinkToolArgs
      const {isError, content: output} = parseContent(thinkArgs.thought)

      return (
        <Tool
          title={'Thought'}
          isError={isError}
          hasPreview
          isInGroup={isInGroup}
          toolHeaderProps={createToolHeaderProps({leadingVisual: toolIcon}, metadata, shouldShowCodeInsights)}
        >
          <ToolPrimaryContent language="markdown" markdown={output} />
        </Tool>
      )
    }

    case TOOL_NAMES.SKILL: {
      const skillArgs = args as SkillToolArgs
      const {isError, content: output} = parseContent(delta.content)
      // When in a group, just show the skill name without "Activate" prefix
      const title = isInGroup ? skillArgs.skill : `Activate ${skillArgs.skill} skill`
      const isPending = output === undefined

      return (
        <Tool
          title={title}
          isError={isError}
          isPending={isPending}
          isInGroup={isInGroup}
          toolHeaderProps={createToolHeaderProps({leadingVisual: toolIcon}, metadata, shouldShowCodeInsights)}
        >
          <ToolPrimaryContent language="markdown" markdown={output} />
        </Tool>
      )
    }

    case TOOL_NAMES.RUN_CUSTOM_SETUP_STEP: {
      const runCustomSetupStepArgs = args as RunCustomSetupStepToolArgs
      const {isError, content: output} = parseContent(delta.content)
      // When in a group, just show the step name without "Run" prefix
      const title = isInGroup
        ? runCustomSetupStepArgs.name?.trim() || 'Custom setup step'
        : !runCustomSetupStepArgs.name?.trim()
          ? 'Run custom setup step'
          : `Run "${runCustomSetupStepArgs.name.trim()}" custom setup step`
      const isPending = output === undefined

      return (
        <Tool
          title={title}
          isError={isError}
          isPending={isPending}
          isInGroup={isInGroup}
          toolHeaderProps={createToolHeaderProps({leadingVisual: toolIcon}, metadata, shouldShowCodeInsights)}
        >
          <ToolPrimaryContent language="console" markdown={output} shellLoading={isPending} />
        </Tool>
      )
    }

    case TOOL_NAMES.RUN_SETUP: {
      const runSetupArgs = args as RunSetupArgs
      const {isError, content: output} = parseContent(delta.content)
      const title = runSetupArgs.name.trim()
      const isPending = output === undefined

      return (
        <Tool
          title={title}
          isError={isError}
          isPending={isPending}
          isInGroup={isInGroup}
          toolHeaderProps={createToolHeaderProps({leadingVisual: toolIcon}, metadata, shouldShowCodeInsights)}
        >
          <ToolPrimaryContent language="console" markdown={output} shellLoading={isPending} />
        </Tool>
      )
    }

    case TOOL_NAMES.STR_REPLACE_EDITOR:
    case TOOL_NAMES.CREATE:
    case TOOL_NAMES.STR_REPLACE:
    case TOOL_NAMES.EDIT:
    case TOOL_NAMES.VIEW: {
      const strReplaceEditorArgs = args as StrReplaceEditorToolArgs

      // If there's no `.command` then the agent is using discrete editing tools, and we'll fallback to `name` as the command
      const command = name === TOOL_NAMES.EDIT ? 'str_replace' : strReplaceEditorArgs.command || name
      // Recheck the icon here in case the tool name was changed
      const editToolIcon = isInGroup ? undefined : getToolIcon(command)

      const fileExtension = strReplaceEditorArgs.path?.split('.').pop() || ''
      // TODO: We need a more robust way to determine the language from the file extension using a pre-existing library mapper
      const language = fileExtension === 'md' ? 'markdown' : fileExtension || 'text'
      const {isError, content: parsedContent} = parseContent(delta.content)
      switch (command) {
        case 'insert':
        case 'undo_edit': {
          const filePath = trimFileName(strReplaceEditorArgs.path)
          const displayPath =
            filePath + getPathSuffix(command, strReplaceEditorArgs.view_range, strReplaceEditorArgs.insert_line)

          const output = extractContentFromDiff(parsedContent)

          return (
            <Tool
              title={getCommandTitle({
                command,
                path: strReplaceEditorArgs.path,
                isInGroup,
              })}
              isError={isError}
              path={filePath ? displayPath : undefined}
              hasPreview={!isInGroup}
              isInGroup={isInGroup}
              toolHeaderProps={createToolHeaderProps({leadingVisual: editToolIcon}, metadata, shouldShowCodeInsights)}
            >
              <ToolPrimaryContent language={language} markdown={output} />
            </Tool>
          )
        }

        case 'create':
        case 'view':
        case 'str_replace': {
          const output = parsedContent?.trim()
          const isViewCommand = command === 'view'
          const filePath = trimFileName(strReplaceEditorArgs.path)
          const displayPath = isViewCommand
            ? filePath + getPathSuffix(command, strReplaceEditorArgs.view_range, strReplaceEditorArgs.insert_line)
            : filePath

          const shouldStartOpen = isInGroup ? (isEditGroup ?? false) : !isViewCommand

          if (output) {
            const diffLines = parseUnifiedDiff(output)

            if (diffLines.length > 0) {
              // For view commands, never collapse as we dont have previews in the first place
              const canCollapse = !isViewCommand

              const diffContainerClassName = isViewCommand
                ? styles.viewDiffContainer
                : command === 'create'
                  ? styles.createDiffContainer
                  : undefined

              return (
                <Tool
                  title={getCommandTitle({
                    command,
                    path: strReplaceEditorArgs.path,
                    isInGroup,
                  })}
                  isError={isError}
                  path={filePath ? displayPath : undefined}
                  hasPreview={false}
                  isInGroup={isInGroup}
                  isOpenOverride={shouldStartOpen}
                  toolHeaderProps={createToolHeaderProps(
                    {leadingVisual: editToolIcon},
                    metadata,
                    shouldShowCodeInsights,
                  )}
                >
                  <div className={diffContainerClassName}>
                    <DiffPreview diffLines={diffLines} canCollapse={canCollapse} />
                  </div>
                </Tool>
              )
            }
          }

          return (
            <Tool
              title={getCommandTitle({
                command,
                path: strReplaceEditorArgs.path,
                isInGroup,
              })}
              isError={isError}
              path={filePath ? displayPath : undefined}
              hasPreview={!isInGroup && !isViewCommand}
              isInGroup={isInGroup}
              isOpenOverride={shouldStartOpen}
              toolHeaderProps={createToolHeaderProps({leadingVisual: editToolIcon}, metadata, shouldShowCodeInsights)}
            >
              <ToolPrimaryContent language={isViewCommand ? language : 'diff'} markdown={output} />
            </Tool>
          )
        }
      }
      // This actually isn't needed, but the linter complains if we don't have it.
      break
    }

    case TOOL_NAMES.REPORT_PROGRESS: {
      const reportProgressArgs = args as ReportProgressToolArgs
      const {isError: outputError, content: output} = parseContent(delta.content)

      if ('prDescription' in reportProgressArgs) {
        const {isError: inputError, content: input} = parseContent(reportProgressArgs.prDescription)

        // Extract only the first line of the commit message for the title
        const firstLineOfCommitMessage =
          reportProgressArgs.commitMessage?.split('\n')[0] || reportProgressArgs.commitMessage || ''

        return (
          <Tool
            title={firstLineOfCommitMessage ? `Progress update: ${firstLineOfCommitMessage}` : 'Progress update'}
            isError={inputError || outputError}
            isInGroup={isInGroup}
            toolHeaderProps={createToolHeaderProps({leadingVisual: toolIcon}, metadata, shouldShowCodeInsights)}
          >
            <ToolPrimaryContent language="markdown" markdown={input} />
            <ToolSecondaryContent type="output" language="json" markdown={output} />
          </Tool>
        )
      } else {
        return (
          <Tool
            title={`Start next task: ${reportProgressArgs.nextTask}`}
            isError={outputError}
            isInGroup={isInGroup}
            toolHeaderProps={createToolHeaderProps({leadingVisual: toolIcon}, metadata, shouldShowCodeInsights)}
          >
            <ToolPrimaryContent language="markdown" markdown={reportProgressArgs.tasksChecklist} />
          </Tool>
        )
      }
    }

    case TOOL_NAMES.COMMIT_CHANGES: {
      const commitChangesArgs = args as CommitChangesToolArgs
      const {isError: outputError, content: output} = parseContent(delta.content)

      // Extract only the first line of the commit message for the title
      const firstLineOfCommitMessage =
        commitChangesArgs.commitMessage?.split('\n')[0] || commitChangesArgs.commitMessage || ''

      return (
        <Tool
          title={firstLineOfCommitMessage ? `Commit changes: ${firstLineOfCommitMessage}` : 'Commit changes'}
          isError={outputError}
          isInGroup={isInGroup}
          toolHeaderProps={createToolHeaderProps({leadingVisual: toolIcon}, metadata, shouldShowCodeInsights)}
        >
          <ToolSecondaryContent type="output" language="json" markdown={output} />
        </Tool>
      )
    }

    // TODO: We have the comment_id in the args, so we can link to the comment in the UI once we have a design
    case TOOL_NAMES.REPLY_TO_COMMENT: {
      const replyToCommentArgs = args as ReplyToCommentToolArgs
      const {isError, content: output} = parseContent(replyToCommentArgs.reply)
      return (
        <Tool
          title={'Reply to comment'}
          isError={isError}
          isInGroup={isInGroup}
          toolHeaderProps={createToolHeaderProps({leadingVisual: toolIcon}, metadata, shouldShowCodeInsights)}
        >
          <ToolPrimaryContent language={'markdown'} markdown={output} />
        </Tool>
      )
    }

    // The remaining tools have been removed, but we need to continue to support them for old sessions
    case TOOL_NAMES.ASYNC_BASH: {
      const asyncBashArgs = args as AsyncBashToolArgs
      const input = `$ ${asyncBashArgs.command}`
      const {isError, content: output} = parseContent(delta.content)
      const isPending = output === undefined

      return (
        <Tool
          title={`Start or send input to long-running Bash session ${asyncBashArgs.shellId || asyncBashArgs.sessionId}`}
          isError={isError}
          isPending={isPending}
          isInGroup={isInGroup}
          toolHeaderProps={createToolHeaderProps({leadingVisual: toolIcon}, metadata, shouldShowCodeInsights)}
        >
          <ToolPrimaryContent
            language="bash"
            markdown={`${input}${output ? `\n${output}` : ''}`}
            shellLoading={isPending}
          />
        </Tool>
      )
    }
    case TOOL_NAMES.READ_ASYNC_BASH: {
      const readAsyncBashArgs = args as ReadAsyncBashToolArgs
      const {isError, content: output} = parseContent(delta.content)
      const isPending = output === undefined

      return (
        <Tool
          title={`View logs from long-running Bash session ${readAsyncBashArgs.shellId || readAsyncBashArgs.sessionId}`}
          isError={isError}
          isPending={isPending}
          isInGroup={isInGroup}
          toolHeaderProps={createToolHeaderProps({leadingVisual: toolIcon}, metadata, shouldShowCodeInsights)}
        >
          <ToolPrimaryContent language="bash" markdown={output} shellLoading={isPending} />
        </Tool>
      )
    }
    case TOOL_NAMES.STOP_ASYNC_BASH: {
      const stopAsyncBashArgs = args as StopAsyncBashToolArgs
      const {isError, content: output} = parseContent(delta.content)
      const isPending = output === undefined

      return (
        <Tool
          title={`Stop long-running Bash session ${stopAsyncBashArgs.shellId || stopAsyncBashArgs.sessionId}`}
          isError={isError}
          isPending={isPending}
          isInGroup={isInGroup}
          toolHeaderProps={createToolHeaderProps({leadingVisual: toolIcon}, metadata, shouldShowCodeInsights)}
        >
          <ToolPrimaryContent language="bash" markdown={output} shellLoading={isPending} />
        </Tool>
      )
    }

    case TOOL_NAMES.LIST_BASH: {
      const {isError, content: output} = parseContent(delta.content)
      const isPending = output === undefined

      return (
        <Tool
          title={`List Bash sessions`}
          isError={isError}
          isPending={isPending}
          isInGroup={isInGroup}
          toolHeaderProps={{leadingVisual: toolIcon}}
        >
          <ToolPrimaryContent language="bash" markdown={output ? output : ''} shellLoading={isPending} />
        </Tool>
      )
    }

    case TOOL_NAMES.RG:
    case TOOL_NAMES.GREP: {
      const grepArgs = (args as GrepToolArgs) ?? {}
      const {isError, content: output} = parseContent(delta.content)
      const isPending = output === undefined
      // When in a group, don't show "Search" since group header already says "Search N times"
      const title = isInGroup ? '' : 'Search'
      const pattern = grepArgs.pattern

      return (
        <Tool
          title={title}
          isError={isError}
          isPending={isPending}
          path={pattern}
          isInGroup={isInGroup}
          toolHeaderProps={createToolHeaderProps({leadingVisual: toolIcon}, metadata, shouldShowCodeInsights)}
        >
          <GrepOutput output={output} outputMode={grepArgs.output_mode} />
        </Tool>
      )
    }

    case TOOL_NAMES.GLOB: {
      const globArgs = (args as GlobToolArgs) ?? {}
      const {isError, content: output} = parseContent(delta.content)
      const isPending = output === undefined
      // When in a group, don't show "Find files" since group header already says it
      const title = isInGroup ? '' : 'Find files'
      const pattern = globArgs.pattern

      return (
        <Tool
          title={title}
          isError={isError}
          isPending={isPending}
          path={pattern}
          isInGroup={isInGroup}
          toolHeaderProps={createToolHeaderProps({leadingVisual: toolIcon}, metadata, shouldShowCodeInsights)}
        >
          <GlobOutput output={output} />
        </Tool>
      )
    }

    case TOOL_NAMES.SEARCH_CODE_SUBAGENT: {
      const searchCodeArgs = (args as SearchCodeSubagentToolArgs) ?? {}
      const {isError, content: output} = parseContent(delta.content)
      const isPending = output === undefined
      const query = searchCodeArgs.query ?? ''
      const title = isInGroup ? query : `Search code: ${query}`

      return (
        <Tool
          title={title}
          isError={isError}
          isPending={isPending}
          isInGroup={isInGroup}
          toolHeaderProps={createToolHeaderProps({leadingVisual: toolIcon}, metadata, shouldShowCodeInsights)}
        >
          <ToolPrimaryContent language="markdown" markdown={output} />
        </Tool>
      )
    }

    case TOOL_NAMES.APPLY_PATCH: {
      const {isError, content: output} = parseContent(delta.content)
      const patchText =
        (typeof args === 'string'
          ? args
          : ((args as {content?: string; patch?: string; arguments?: string})?.arguments ??
            (args as {content?: string; patch?: string})?.content ??
            (args as {patch?: string})?.patch)) ?? ''

      const operations = parseApplyPatch(patchText)

      const renderOperationContent = (op: ApplyPatchOperation) => {
        const path = trimFileName(op.path) ?? 'unknown file'
        switch (op.type) {
          case 'add': {
            // Create a faux unified diff with all additions
            const content = op.lines.map(line => (line.startsWith('+') ? line.slice(1) : line)).join('\n')
            const fauxDiff = [
              `diff --git a/${path} b/${path}`,
              'new file mode 100644',
              `--- /dev/null`,
              `+++ b/${path}`,
              '@@ @@',
              ...content.split('\n').map(line => `+${line}`),
            ].join('\n')

            const diffLines = parseUnifiedDiff(fauxDiff).map(line => ({...line, left: null, right: null}))
            if (diffLines.length > 0) {
              return <StaticUnifiedDiffPreview diffLines={diffLines} tabSize={4} className={styles.diffPreview} />
            }

            // Fallback to markdown rendering
            return <ToolPrimaryContent language="diff" markdown={fauxDiff} />
          }
          case 'delete': {
            return <ToolPrimaryContent language="text" markdown="File deleted" />
          }
          case 'update': {
            // Convert the custom patch format to unified diff format
            const unifiedDiff = convertPatchToUnifiedDiff(op)

            // Use StaticUnifiedDiffPreview
            if (unifiedDiff) {
              // TODO: Update the output from the apply_patch tool to return proper unified diffs directly
              const diffLines = parseUnifiedDiff(unifiedDiff).map(line => ({...line, left: null, right: null}))

              if (diffLines.length > 0) {
                return <StaticUnifiedDiffPreview diffLines={diffLines} tabSize={4} className={styles.diffPreview} />
              }
            }

            // Fallback to markdown rendering
            return <ToolPrimaryContent language="diff" markdown={unifiedDiff || output} />
          }
          default:
            return <ToolPrimaryContent language="text" markdown={output} />
        }
      }

      const getOperationAction = (op: ApplyPatchOperation | undefined) => {
        if (!op) return undefined
        switch (op.type) {
          case 'add':
            return 'Create'
          case 'delete':
            return 'Delete'
          case 'update':
            return 'Edit'
          default:
            return `Apply patch (${(op as {type: string | undefined}).type ?? 'unknown'})`
        }
      }

      const getOperationPath = (op: ApplyPatchOperation | undefined) => {
        if (!op) return undefined
        const path = trimFileName(op.path)
        // For renames, just show the new path in the header
        if (op.type === 'update' && op.newPath) {
          return trimFileName(op.newPath)
        }
        return path
      }

      const renderOperations = () => {
        if (operations.length === 0) {
          return <ToolPrimaryContent language="text" markdown={patchText || output} />
        }

        // For a single operation, skip the inner title since it's shown in the header
        // But show rename info if applicable
        if (operations.length === 1) {
          const op = operations[0]
          if (!op) return <ToolPrimaryContent language="text" markdown={patchText || output} />
          const isRename = op.type === 'update' && op.newPath
          return (
            <div className="border-top tmp-px-3 py-2">
              {isRename && (
                <span className="d-block fgColor-muted mb-2 text-small">Renamed from {trimFileName(op.path)}</span>
              )}
              {renderOperationContent(op)}
            </div>
          )
        }

        return operations.map(op => (
          <div
            key={`${op.type}-${op.path}-${(op as {newPath?: string}).newPath ?? ''}`}
            className="border-top tmp-px-3 py-2"
          >
            <Text as="div" weight="semibold" className="mb-2">
              {getOperationAction(op)} <span className="fgColor-muted">{getOperationPath(op)}</span>
              {op.type === 'update' && op.newPath && (
                <span className="fgColor-muted text-small"> (from {trimFileName(op.path)})</span>
              )}
            </Text>
            {renderOperationContent(op)}
          </div>
        ))
      }

      let title = 'Edit file(s)'
      let filePath: string | undefined
      if (operations.length === 1) {
        const firstOp = operations[0]
        if (firstOp) {
          // When in a group, just show the path without action prefix
          title = isInGroup ? '' : (getOperationAction(firstOp) ?? title)
          filePath = getOperationPath(firstOp)
        }
      }

      return (
        <Tool
          title={title}
          isError={isError}
          path={filePath}
          isInGroup={isInGroup}
          toolHeaderProps={createToolHeaderProps({leadingVisual: toolIcon}, metadata, shouldShowCodeInsights)}
        >
          {renderOperations()}
        </Tool>
      )
    }

    case TOOL_NAMES.CODE_REVIEW: {
      const {isError, content} = parseContent(delta.content)
      const title = CODE_REVIEW_TITLE
      const isPending = content === undefined
      return (
        <Tool
          title={title}
          isError={isError}
          isPending={isPending}
          isInGroup={isInGroup}
          toolHeaderProps={createToolHeaderProps({leadingVisual: toolIcon}, metadata, shouldShowCodeInsights)}
        >
          <ToolPrimaryContent language="markdown" markdown={content} />
        </Tool>
      )
    }

    case TOOL_NAMES.PROPOSE_WORK: {
      const proposeWorkArgs = args as ProposeWorkToolArgs
      const {isError} = parseContent(delta.content)
      // When in a group, just show the work title without "Propose work" prefix
      const title = isInGroup ? proposeWorkArgs.workTitle : `Propose work: ${proposeWorkArgs.workTitle}`
      return (
        <Tool
          title={title}
          isError={isError}
          hasPreview={false}
          isInGroup={isInGroup}
          isOpenOverride
          toolHeaderProps={isInGroup ? undefined : {leadingVisual: getToolIcon(name)}}
        >
          <ToolProposeWork
            title={proposeWorkArgs.workTitle}
            description={proposeWorkArgs.workDescription}
            type={proposeWorkArgs.workType}
          />
        </Tool>
      )
    }

    case TOOL_NAMES.CODEQL_CHECKER: {
      const {isError, content} = parseContent(delta.content)
      const isPending = content === undefined
      return (
        <Tool
          title={CODEQL_CHECKER_TITLE}
          isError={isError}
          isPending={isPending}
          isInGroup={isInGroup}
          toolHeaderProps={createToolHeaderProps({leadingVisual: toolIcon}, metadata, shouldShowCodeInsights)}
        >
          <ToolPrimaryContent language="markdown" markdown={content} />
        </Tool>
      )
    }

    case TOOL_NAMES.PARALLEL_VALIDATION: {
      const {isError, content} = parseContent(delta.content)
      const isPending = content === undefined
      return (
        <Tool
          title={PARALLEL_VALIDATION_TITLE}
          isError={isError}
          isPending={isPending}
          isInGroup={isInGroup}
          toolHeaderProps={createToolHeaderProps({leadingVisual: toolIcon}, metadata, shouldShowCodeInsights)}
        >
          <ToolPrimaryContent language="markdown" markdown={content} />
        </Tool>
      )
    }

    case TOOL_NAMES.TASK_COMPLETE: {
      const taskCompleteArgs = (args ?? {}) as TaskCompleteToolArgs
      return (
        <Tool
          title="Task complete"
          isError={false}
          collapsible={false}
          bordered={false}
          toolHeaderProps={{leadingVisual: toolIcon}}
        >
          <ToolPrimaryContent language="markdown" markdown={taskCompleteArgs.summary ?? ''} />
        </Tool>
      )
    }

    case TOOL_NAMES.TASK: {
      // Task tool calls are rendered as CustomAgentSessionGroup entries instead,
      // so hide the duplicate here.
      return null
    }

    case TOOL_NAMES.CREATE_PULL_REQUEST: {
      const createPullRequestArgs = args as CreatePullRequestToolArgs
      const {isError, content} = parseContent(delta.content)
      const {language: outputLanguage, output} = parseGenericToolOutput(content)
      const input =
        Object.keys(createPullRequestArgs).length === 0 ? null : JSON.stringify(createPullRequestArgs, null, 2)
      const prefix = createPullRequestArgs.draft ? 'Create draft pull request' : 'Create pull request'
      const title = createPullRequestArgs.title ? `${prefix}: ${createPullRequestArgs.title}` : prefix
      const isPending = output === undefined

      return (
        <Tool
          title={title}
          isError={isError}
          isPending={isPending}
          isInGroup={isInGroup}
          toolHeaderProps={createToolHeaderProps({leadingVisual: toolIcon}, metadata, shouldShowCodeInsights)}
        >
          {input && <ToolSecondaryContent type="input" language="json" markdown={input} />}
          <ToolPrimaryContent language={outputLanguage} markdown={output} />
        </Tool>
      )
    }

    case TOOL_NAMES.READ_AGENT: {
      const readAgentArgs = (args ?? {}) as ReadAgentToolArgs
      const {isError, content: output} = parseContent(delta.content)
      const isPending = output === undefined
      const parsed = parseAgentStatusLog(output)
      const title = buildReadAgentTitle(parsed, readAgentArgs.agent_id)

      return (
        <Tool
          title={title}
          isError={isError}
          isPending={isPending}
          isInGroup={isInGroup}
          toolHeaderProps={createToolHeaderProps({leadingVisual: toolIcon}, metadata, shouldShowCodeInsights)}
        >
          <ToolPrimaryContent language="markdown" markdown={output} />
        </Tool>
      )
    }

    case TOOL_NAMES.WRITE_AGENT: {
      const writeAgentArgs = (args ?? {}) as WriteAgentToolArgs
      const {isError, content: output} = parseContent(delta.content)
      const isPending = output === undefined
      const parsed = parseAgentStatusLog(output)
      const title = buildWriteAgentTitle(parsed, writeAgentArgs.agent_id)

      return (
        <Tool
          title={title}
          isError={isError}
          isPending={isPending}
          isInGroup={isInGroup}
          toolHeaderProps={createToolHeaderProps({leadingVisual: toolIcon}, metadata, shouldShowCodeInsights)}
        >
          {writeAgentArgs.message && (
            <ToolSecondaryContent type="input" language="markdown" markdown={writeAgentArgs.message} />
          )}
          <ToolPrimaryContent language="markdown" markdown={output} />
        </Tool>
      )
    }

    case TOOL_NAMES.LIST_AGENTS: {
      const {isError, content: output} = parseContent(delta.content)
      const isPending = output === undefined

      return (
        <Tool
          title="List agents"
          isError={isError}
          isPending={isPending}
          isInGroup={isInGroup}
          toolHeaderProps={createToolHeaderProps({leadingVisual: toolIcon}, metadata, shouldShowCodeInsights)}
        >
          <ToolPrimaryContent language="markdown" markdown={output} />
        </Tool>
      )
    }

    // These tools are rendered via dedicated UI (loading indicator, interactive prompts)
    // so hide them from the regular tool call timeline.
    case TOOL_NAMES.REPORT_INTENT:
    case 'ask_user':
    case 'exit_plan_mode':
    case 'permission_request':
      return null

    default: {
      const {isError, content} = parseContent(delta.content)
      const {language: outputLanguage, output} = parseGenericToolOutput(content)
      const inputObj = args ?? {}
      const input = Object.keys(inputObj).length === 0 ? null : JSON.stringify(inputObj, null, 2)
      const mcpDisplayName = mcpToolNameMappings?.[name]
      const title = TOOL_CALL_TITLES[name] || mcpDisplayName || `Call to ${name}`
      const isPending = output === undefined

      return (
        <Tool
          title={title}
          isError={isError}
          isPending={isPending}
          isInGroup={isInGroup}
          toolHeaderProps={createToolHeaderProps({leadingVisual: toolIcon}, metadata, shouldShowCodeInsights)}
        >
          {input && <ToolSecondaryContent type="input" language="json" markdown={input} />}
          <ToolPrimaryContent language={outputLanguage} markdown={output} />
        </Tool>
      )
    }
  }
}

const parseGenericToolOutput = (content: string | undefined): {language: string; output: string | undefined} => {
  if (typeof content === 'undefined') {
    return {language: 'text', output: content}
  }

  try {
    const parsedOutput = JSON.parse(content)
    const output = JSON.stringify(parsedOutput, null, 2)
    return {language: 'json', output}
  } catch {
    return {language: 'text', output: content}
  }
}

function shouldWrapInCodeBlock(language: string): boolean {
  return !(language === 'markdown' || language === 'text' || language === '')
}

export type ApplyPatchOperation =
  | {type: 'add'; path: string; lines: string[]}
  | {type: 'delete'; path: string}
  | {type: 'update'; path: string; newPath?: string; lines: string[]}

export function parseApplyPatch(patchText: string): ApplyPatchOperation[] {
  const lines = patchText.split(/\r?\n/)
  const operations: ApplyPatchOperation[] = []
  let inPatch = false
  let currentOp: ApplyPatchOperation | null = null

  const pushCurrent = () => {
    if (currentOp) {
      operations.push(currentOp)
      currentOp = null
    }
  }

  for (const line of lines) {
    if (line.startsWith('*** Begin Patch')) {
      inPatch = true
      continue
    }
    if (!inPatch) continue
    if (line.startsWith('*** End Patch')) {
      pushCurrent()
      break
    }
    if (line.startsWith('*** Add File: ')) {
      pushCurrent()
      currentOp = {type: 'add', path: line.replace('*** Add File: ', '').trim(), lines: []}
      continue
    }
    if (line.startsWith('*** Delete File: ')) {
      pushCurrent()
      currentOp = {type: 'delete', path: line.replace('*** Delete File: ', '').trim()}
      continue
    }
    if (line.startsWith('*** Update File: ')) {
      pushCurrent()
      currentOp = {type: 'update', path: line.replace('*** Update File: ', '').trim(), lines: []}
      continue
    }
    if (line.startsWith('*** Move to: ') && currentOp && currentOp.type === 'update') {
      currentOp.newPath = line.replace('*** Move to: ', '').trim()
      continue
    }
    if (currentOp && 'lines' in currentOp) {
      currentOp.lines.push(line)
    }
  }

  return operations
}

/**
 * Converts the custom patch format lines to unified diff format
 */
function convertPatchToUnifiedDiff(op: ApplyPatchOperation & {type: 'update'}): string {
  if (!op.lines || op.lines.length === 0) return ''

  const path = op.path
  const newPath = op.newPath || path
  const lines: string[] = []

  // Add unified diff header
  lines.push(`diff --git a/${path} b/${newPath}`)
  lines.push(`--- a/${path}`)
  lines.push(`+++ b/${newPath}`)

  // Add a generic @@ line since apply_patch doesn't have real line numbers
  lines.push('@@ @@')

  // Add the diff content lines (skip any existing @@ line from the patch)
  for (const line of op.lines) {
    // Skip the @@ line if it exists in the source
    if (line.startsWith('@@')) {
      continue
    }
    // Lines starting with +, -, or space are already in the right format
    if (line.startsWith('+') || line.startsWith('-') || line.startsWith(' ')) {
      lines.push(line)
    } else {
      // Context lines (no prefix) should have a space prefix
      lines.push(` ${line}`)
    }
  }

  return lines.join('\n')
}

export function Tool({
  title,
  isError = false,
  isPending = false,
  path,
  hasPreview = false,
  collapseLargeContent = false,
  collapsible = true,
  bordered = true,
  toolHeaderProps,
  children,
  isOpenOverride,
  isInGroup = false,
}: {
  title: string
  isError: boolean
  isPending?: boolean
  path?: string
  hasPreview?: boolean
  collapseLargeContent?: boolean
  /** When false, content is always visible and the header has no toggle affordance */
  collapsible?: boolean
  /** When false, the tool renders without a border/background container */
  bordered?: boolean
  toolHeaderProps?: ToolHeaderPropsType
  children: ReactNode
  isOpenOverride?: boolean
  isInGroup?: boolean
}) {
  const {variant} = useSessionLogsContext()
  const animationEnabled = isFeatureEnabled(COLLAPSE_ANIMATION_FLAG)

  // Use isOpenOverride as the initial value, but allow user to toggle manually
  const hasUserToggledRef = useRef(false)
  const [isToolContentOpen, setIsToolContentOpen] = useState(collapsible ? (isOpenOverride ?? false) : true)
  const prevIsOpenOverrideRef = useRef(isOpenOverride)
  const contentRef = useRef<HTMLDivElement>(null)
  const [shouldShowPreview, setShouldShowPreview] = useState(hasPreview)

  // Sync with isOpenOverride changes, but only if user hasn't manually toggled
  useEffect(() => {
    if (prevIsOpenOverrideRef.current !== isOpenOverride) {
      prevIsOpenOverrideRef.current = isOpenOverride
      // Only apply isOpenOverride if user hasn't manually toggled
      if (!hasUserToggledRef.current && isOpenOverride !== undefined) {
        setIsToolContentOpen(isOpenOverride)
      }
    }
  }, [isOpenOverride])

  // Check content height to determine if preview should be shown
  useEffect(() => {
    if (!hasPreview || !contentRef.current) {
      setShouldShowPreview(hasPreview)
      return
    }

    // Check content height after render
    const contentHeight = contentRef.current.scrollHeight
    // If content is less than threshold, show it fully expanded by default
    if (contentHeight < TOOL_PREVIEW_HEIGHT_THRESHOLD) {
      setShouldShowPreview(false)
      // Auto-expand if content is small
      if (!hasUserToggledRef.current) {
        setIsToolContentOpen(true)
      }
    } else {
      setShouldShowPreview(!collapseLargeContent)
      if (collapseLargeContent && !hasUserToggledRef.current) {
        setIsToolContentOpen(false)
      }
    }
  }, [hasPreview, collapseLargeContent])

  const toolInstanceRef = useRef({})

  const handlerRef = useRef<((event: Event) => void) | null>(null)

  // eslint-disable-next-line react-hooks/refs
  handlerRef.current = (event: Event) => {
    const customEvent = event as CustomEvent<{expand: boolean; excludeInstance: object}>
    if (customEvent.detail.excludeInstance !== toolInstanceRef.current) {
      // Update React state
      const shouldExpand = customEvent.detail.expand
      setIsToolContentOpen(shouldExpand)
    }
  }

  // Listen for global expand/collapse events
  useEffect(() => {
    const handler = (event: Event) => {
      handlerRef.current?.(event)
    }

    document.addEventListener('mission-control-tool-global-toggle', handler)
    return () => {
      document.removeEventListener('mission-control-tool-global-toggle', handler)
    }
  }, [])

  const handleToggle = () => {
    if (!collapsible) return
    // Mark that user has manually toggled
    hasUserToggledRef.current = true
    setIsToolContentOpen(prev => {
      const newValue = !prev
      return newValue
    })
  }
  const handleModifierClick = (expand: boolean) => {
    if (!collapsible) return
    // Mark that user has manually toggled
    hasUserToggledRef.current = true

    // Update React state
    setIsToolContentOpen(expand)

    // Dispatch event to all other tools
    const event = new CustomEvent('mission-control-tool-global-toggle', {
      detail: {expand, excludeInstance: toolInstanceRef.current},
    })
    document.dispatchEvent(event)
  }

  // Check if there are any meaningful children to determine if we have references
  // Filter out null, undefined, false, and empty values
  const hasReferences = collapsible && Children.toArray(children).filter(Boolean).length > 0

  return (
    <div
      className={clsx(styles.toolContainer, {
        [styles.toolContainerDefault]: bordered && variant === 'default',
        [styles.toolContainerBorderless]: !bordered,
        [styles.toolContainerInGroup]: isInGroup,
        [styles.toolContainerExpanded]: isToolContentOpen,
      })}
      style={{'--tool-preview-height-threshold': `${TOOL_PREVIEW_HEIGHT_THRESHOLD}px`}}
    >
      <NewToolHeader
        leadingVisual={toolHeaderProps?.leadingVisual}
        trailingVisual={toolHeaderProps?.trailingVisual}
        title={title}
        path={path}
        isCollapsed={!isToolContentOpen}
        isPending={isPending}
        isError={isError}
        isInGroup={isInGroup}
        onToggle={handleToggle}
        onModifierClick={handleModifierClick}
        hasReferences={hasReferences}
      />
      {/* Full content - only visible when full view is expanded; preview when collapsed (with hasPreview).
          With the animation flag on, the non-preview collapse is animated via CollapsibleRegion (children
          stay mounted, as before) and the preview path is left untouched. With the flag off we render the
          original always-mounted div so the un-animated behavior is byte-for-byte what it was before. */}
      {animationEnabled ? (
        shouldShowPreview ? (
          <div
            ref={contentRef}
            className={clsx(
              {
                [styles.toolContentPreview]: !isToolContentOpen,
                [styles.toolContentMissionControl]: bordered && isToolContentOpen && variant === 'mission-control',
                [styles.toolContentBorderless]: !bordered && isToolContentOpen,
              },
              bordered && isToolContentOpen && variant === 'default' && 'border-top',
              bordered && isToolContentOpen && variant === 'default' && styles.toolContent,
            )}
            {...(!isToolContentOpen ? {inert: true} : {})}
          >
            {children}
          </div>
        ) : (
          <CollapsibleRegion open={isToolContentOpen} keepMounted>
            <div
              ref={contentRef}
              className={clsx(
                {
                  [styles.toolContentMissionControl]: bordered && variant === 'mission-control',
                  [styles.toolContentBorderless]: !bordered,
                },
                bordered && variant === 'default' && 'border-top',
                bordered && variant === 'default' && styles.toolContent,
              )}
            >
              {children}
            </div>
          </CollapsibleRegion>
        )
      ) : (
        <div
          ref={contentRef}
          className={clsx(
            {
              // Collapsed states
              [styles.toolContentPreview]: !isToolContentOpen && shouldShowPreview,
              [styles.toolContentCollapsed]: !isToolContentOpen && !shouldShowPreview,
              // Expanded states - 'chat' variant applies no additional border or container styling when expanded
              [styles.toolContentMissionControl]: bordered && isToolContentOpen && variant === 'mission-control',
              [styles.toolContentBorderless]: !bordered && isToolContentOpen,
            },
            bordered && isToolContentOpen && variant === 'default' && 'border-top',
            bordered && isToolContentOpen && variant === 'default' && styles.toolContent,
          )}
          {...(!isToolContentOpen ? {inert: true} : {})}
        >
          {children}
        </div>
      )}
    </div>
  )
}

export function ToolPrimaryContent({
  language,
  markdown,
  ...extensionProps
}: {
  language: string
  markdown: string | undefined
} & ToolCodeBlockExtensionArgs) {
  const {markdown: truncatedMarkdown} = truncateMarkdownContent(markdown)

  return markdown === undefined ? (
    <MarkdownLoading />
  ) : (
    <MarkdownRenderer
      markdown={
        shouldWrapInCodeBlock(language)
          ? wrapInCodeBlock({language, markdown: truncatedMarkdown || ''})
          : truncatedMarkdown || ''
      }
      extensions={[toolCodeBlockExtension({...extensionProps})]}
      // Code gets rendered with padding so we need to apply padding to content not being wrapped in a code block
      className={clsx(!shouldWrapInCodeBlock(language) && 'tmp-p-3')}
    />
  )
}

function ToolSecondaryContent({
  type,
  language,
  markdown,
}: {
  type: 'input' | 'output'
  language: string
  markdown: string | undefined
}) {
  const {markdown: truncatedMarkdown} = truncateMarkdownContent(markdown)

  return (
    <Stack direction="vertical" padding="normal" gap="condensed">
      <Text size="small" weight="medium" className={'fgColor-muted'}>
        {type === 'input' ? 'Input' : 'Output'}
      </Text>
      {markdown === undefined ? (
        <MarkdownLoading />
      ) : (
        <MarkdownRenderer
          // Code gets rendered with padding so we need to apply padding to content not being wrapped in a code block
          className={clsx(!shouldWrapInCodeBlock && 'tmp-p-3', 'border rounded bgColor-default overflow-hidden')}
          markdown={wrapInCodeBlock({
            language,
            markdown: truncatedMarkdown || '',
          })}
          extensions={[toolCodeBlockExtension()]}
        />
      )}
    </Stack>
  )
}

function MarkdownLoading({lines = 3, maxWidth = '45ch'}: {lines?: number; maxWidth?: string}) {
  return (
    // Padding matches the default padding of the MarkdownRenderer
    <div className="tmp-py-3 tmp-px-4">
      {/* eslint-disable-next-line primer-react/no-system-props */}
      <SkeletonText lines={lines} maxWidth={maxWidth} />
    </div>
  )
}

// Parses the delta.content for <error> tags to detect errors and remove tags from content
export function parseContent(content?: string): {isError: boolean; content: string | undefined} {
  if (!content) return {isError: false, content}

  const startTag = `<${'error'}>`
  const endTag = `</${'error'}>`
  const startIndex = content.indexOf(startTag)
  const endIndex = startIndex !== -1 ? content.indexOf(endTag, startIndex + startTag.length) : -1
  const match = startIndex !== -1 && endIndex !== -1 ? content.slice(startIndex + startTag.length, endIndex) : null

  if (match !== null) {
    return {
      isError: true,
      content: match,
    }
  }

  return {isError: false, content}
}

const getCommandTitle = ({
  command,
  path,
  isInGroup,
}: {
  command: StrReplaceEditorToolArgs['command']
  path: string
  isInGroup?: boolean
}) => {
  const hasPath = !!trimFileName(path)?.length

  // When in a group, don't show the command prefix since the group header already shows the action
  if (isInGroup) {
    return ''
  }

  switch (command) {
    case 'create':
      return 'Create'
    case 'insert':
      return 'Insert lines at'
    case 'view':
      return `View${hasPath ? '' : ' repository'}`
    case 'str_replace':
      return 'Edit'
    case 'undo_edit':
      return 'Undo edit'
    default:
      return command || 'Unknown'
  }
}

const getPathSuffix = (command: StrReplaceEditorToolArgs['command'], viewRange?: number[], insertLine?: number) => {
  if (command === 'view' && viewRange) {
    // Hide line numbers when viewing the full file (1 to -1)
    if (viewRange[0] === 1 && viewRange[1] === -1) {
      return ''
    }
    return `:${viewRange[0]}-${viewRange[1]}`
  }

  if (command === 'insert' && insertLine !== undefined) {
    return `:${insertLine}`
  }

  return ''
}

// Maximum characters to display for a single matched line before truncating
const GREP_LINE_MAX_LENGTH = 200

/**
 * Component to display grep/rg output in a structured way.
 * Renders differently based on the output_mode arg:
 * - "files_with_matches" (default): clean file path list
 * - "count": file paths with match counts
 * - "content": structured code view with line numbers
 */
export function GrepOutput({output, outputMode}: {output: string | undefined; outputMode?: string}) {
  if (!output) {
    return <MarkdownLoading />
  }

  // Handle "No matches found." response
  if (output.trim() === 'No matches found.' || output.trim() === 'No matches found') {
    return (
      <div className="tmp-p-3">
        <span className="fgColor-muted text-small">No matches found</span>
      </div>
    )
  }

  // Parse grep/rg output
  const lines = output.split('\n').filter(line => line.trim())

  // files_with_matches mode (default): render as a simple file list
  if (outputMode === 'files_with_matches') {
    return <FileListOutput lines={lines} />
  }

  // Check if this is count mode output (filename:count format with only one colon and a number)
  const isCountMode =
    lines.length > 0 &&
    lines.every(line => {
      const match = line.match(/^([^:]+):(\d+)$/)
      return match !== null
    })

  if (isCountMode) {
    // Parse count mode: filename:count
    const counts: Array<{file: string; count: number}> = []
    for (const line of lines) {
      const match = line.match(/^([^:]+):(\d+)$/)
      if (match) {
        counts.push({
          file: match[1] ?? '',
          count: parseInt(match[2] ?? '0', 10),
        })
      }
    }

    return (
      <div className="tmp-p-3">
        <span className="fgColor-muted mb-2 d-block text-small">
          {counts.length} <span>{counts.length === 1 ? 'file' : 'files'}</span> with matches
        </span>
        <div className="border rounded bgColor-default overflow-hidden">
          {counts.map(({file, count}, idx) => (
            <div
              key={idx}
              className={clsx('tmp-px-3 py-2 d-flex text-small', idx > 0 && 'border-top')}
              style={{
                fontFamily: 'var(--fontStack-monospace)',
              }}
            >
              <span className="flex-1">{trimFileName(file)}</span>
              <span className="fgColor-muted ml-2">
                {count} <span>{count === 1 ? 'match' : 'matches'}</span>
              </span>
            </div>
          ))}
        </div>
      </div>
    )
  }

  // Content mode: parse line-by-line matches
  const fileGroups = new Map<string, Array<{lineNumber?: string; content: string; isContext?: boolean}>>()
  let currentFile = ''

  for (const line of lines) {
    // Skip separator lines
    if (line === '--') continue

    // Match format: linenumber:content or linenumber-content (without filename prefix)
    const lineOnlyColonMatch = line.match(/^(\d+):(.*)$/)
    const lineOnlyDashMatch = line.match(/^(\d+)-(.*)$/)

    // Match format: filename:linenumber:content or filename-linenumber-content (with filename prefix)
    const fullColonMatch = line.match(/^([^:]+):(\d+):(.*)$/)
    const fullDashMatch = line.match(/^([^-]+)-(\d+)-(.*)$/)

    if (fullColonMatch) {
      // This is a match line with filename
      currentFile = fullColonMatch[1] ?? ''
      if (!fileGroups.has(currentFile)) {
        fileGroups.set(currentFile, [])
      }
      fileGroups.get(currentFile)?.push({
        lineNumber: fullColonMatch[2],
        content: fullColonMatch[3] ?? '',
        isContext: false,
      })
    } else if (fullDashMatch) {
      // This is a context line with filename
      currentFile = fullDashMatch[1] ?? ''
      if (!fileGroups.has(currentFile)) {
        fileGroups.set(currentFile, [])
      }
      fileGroups.get(currentFile)?.push({
        lineNumber: fullDashMatch[2],
        content: fullDashMatch[3] ?? '',
        isContext: true,
      })
    } else if (lineOnlyColonMatch && currentFile) {
      // This is a match line without filename (uses current file)
      fileGroups.get(currentFile)?.push({
        lineNumber: lineOnlyColonMatch[1],
        content: lineOnlyColonMatch[2] ?? '',
        isContext: false,
      })
    } else if (lineOnlyDashMatch && currentFile) {
      // This is a context line without filename (uses current file)
      fileGroups.get(currentFile)?.push({
        lineNumber: lineOnlyDashMatch[1],
        content: lineOnlyDashMatch[2] ?? '',
        isContext: true,
      })
    } else {
      // This might be a file header line (files_with_matches output without explicit mode)
      currentFile = line
      if (!fileGroups.has(currentFile)) {
        fileGroups.set(currentFile, [])
      }
    }
  }

  // If parsing produced only file headers with no matched content lines,
  // render as a file list instead of showing empty groups
  const hasAnyContent = Array.from(fileGroups.values()).some(matches => matches.length > 0)
  if (!hasAnyContent && fileGroups.size > 0) {
    return <FileListOutput lines={Array.from(fileGroups.keys())} />
  }

  return (
    <div className="tmp-p-3">
      {fileGroups.size > 0 ? (
        Array.from(fileGroups.entries()).map(([file, matches]) => (
          <div key={file} className="tmp-mb-3">
            <span className="fgColor-muted mb-2 d-block text-small">{trimFileName(file)}</span>
            <div className="border rounded bgColor-default overflow-hidden">
              {matches.map((match, idx) => (
                <div
                  key={idx}
                  className={clsx(
                    'tmp-px-3 py-1 text-mono text-small',
                    match.isContext && 'fgColor-muted',
                    idx > 0 && 'border-top',
                  )}
                >
                  {match.lineNumber && (
                    <span className="fgColor-muted" style={{minWidth: '3em', display: 'inline-block'}}>
                      {match.lineNumber}:
                    </span>
                  )}
                  <span style={{whiteSpace: 'pre-wrap'}}>{truncateMatchContent(match.content)}</span>
                </div>
              ))}
            </div>
          </div>
        ))
      ) : (
        <span className="fgColor-muted text-small">No matches found</span>
      )}
    </div>
  )
}

/**
 * Truncates a matched content line if it exceeds the max display length.
 */
function truncateMatchContent(content: string): string {
  if (content.length <= GREP_LINE_MAX_LENGTH) return content
  return `${content.slice(0, GREP_LINE_MAX_LENGTH)}…`
}

/**
 * Renders a list of file paths (used for files_with_matches mode and fallback)
 */
function FileListOutput({lines}: {lines: string[]}) {
  if (lines.length === 0) {
    return (
      <div className="tmp-p-3">
        <span className="fgColor-muted">No files found</span>
      </div>
    )
  }

  return (
    <div className="tmp-p-3">
      <span className="fgColor-muted mb-2 d-block text-small">
        {lines.length} <span>{lines.length === 1 ? 'file' : 'files'}</span> matched
      </span>
      <ul className="list-style-none m-0 p-0">
        {lines.map((file, idx) => (
          <li key={idx} className="text-mono text-small">
            {trimFileName(file)}
          </li>
        ))}
      </ul>
    </div>
  )
}

/**
 * Component to display glob output in a structured way
 */
function GlobOutput({output}: {output: string | undefined}) {
  if (!output) {
    return <MarkdownLoading />
  }

  // Parse glob output - one file per line
  const files = output.split('\n').filter(f => f.trim())

  if (files.length === 0) {
    return (
      <div className="tmp-p-3">
        <span className="fgColor-muted">No files found</span>
      </div>
    )
  }

  return (
    <div className="tmp-p-3">
      <span className="fgColor-muted mb-2 d-block text-small">
        {files.length} <span>{files.length === 1 ? 'file' : 'files'}</span> found
      </span>
      <ul className="list-style-none m-0 p-0">
        {files.map((file, idx) => (
          <li key={idx} className="text-mono text-small">
            {trimFileName(file)}
          </li>
        ))}
      </ul>
    </div>
  )
}
